import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Shield } from "lucide-react";

// Post-payment confirmation overlay. Shows 3 green "expert" check cards with
// a stagger animation, an animated score counter (preliminary → final), and
// a trust message. Auto-resolves to the final analysis after ~5.2s.
export const ExpertConfirmationOverlay = ({ state, onComplete, t }) => {
  const { visible, from, to } = state;
  const [count, setCount] = useState(0);

  const fromScore = from?.worthify_score?.score ?? 0;
  const toScore = to?.worthify_score?.score ?? 0;
  const verdictChanged = (from?.risk_level || '') !== (to?.risk_level || '');

  useEffect(() => {
    if (!visible) return;
    setCount(fromScore);
    const duration = 2200;
    const start = Date.now();
    const diff = toScore - fromScore;
    const iv = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(fromScore + diff * eased));
      if (p >= 1) clearInterval(iv);
    }, 40);
    const t1 = setTimeout(() => onComplete && onComplete(to), 5200);
    return () => { clearInterval(iv); clearTimeout(t1); };
  }, [visible, fromScore, toScore, onComplete, to]);

  if (!to) return null;

  const accent = to.risk_level === 'LOW' ? '#22c55e'
    : to.risk_level === 'HIGH' ? '#ef4444' : '#fbbf24';

  return (
    <AnimatePresence>
      {visible && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        style={{ position: 'fixed', inset: 0, zIndex: 310, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        data-testid="expert-confirmation-overlay"
      >
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.95) 100%)' }} />
        <motion.div
          initial={{ scale: 0.94, y: 8 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '2rem', maxWidth: '520px', width: '100%' }}
        >
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.35rem 0.9rem', borderRadius: '999px', marginBottom: '1.3rem',
              background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)',
            }}
          >
            <Shield className="w-3.5 h-3.5" style={{ color: "var(--verdict-color-low)" }} />
            <span style={{ color: "var(--verdict-color-low)", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t('expertConfirmBadge')}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            style={{ color: "var(--wf-modal-text)", fontSize: 'clamp(1.5rem, 4.5vw, 2rem)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.55rem', lineHeight: 1.15 }}
          >
            {t('expertConfirmTitle')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
            style={{ color: "var(--wf-modal-text-muted)", fontSize: '0.92rem', marginBottom: '1.7rem' }}
          >
            {t('expertConfirmSubtitle')}
          </motion.p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.6rem', marginBottom: '1.9rem' }}>
            {['aiRoleEngine', 'aiRolePlatform', 'aiRoleRisk'].map((role, i) => (
              <motion.div
                key={role}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.5 + i * 0.18, type: 'spring', stiffness: 260, damping: 18 }}
                style={{
                  background: 'rgba(34,197,94,0.08)',
                  border: '1px solid rgba(34,197,94,0.35)',
                  borderRadius: '14px',
                  padding: '0.85rem 0.5rem',
                  textAlign: 'center',
                  boxShadow: '0 0 22px rgba(34,197,94,0.15)',
                }}
                data-testid={`expert-confirm-card-${i}`}
              >
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.55 + i * 0.18, type: 'spring', stiffness: 380, damping: 16 }}
                  style={{ margin: '0 auto 0.4rem', display: 'inline-flex' }}
                >
                  <CheckCircle2 className="w-5 h-5" style={{ color: "var(--verdict-color-low)" }} />
                </motion.div>
                <div style={{ color: "var(--wf-modal-text-muted)", fontWeight: 600, fontSize: '0.74rem', letterSpacing: '-0.005em', lineHeight: 1.2 }}>
                  {t(role)}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.45rem' }}
          >
            <span style={{ color: "var(--wf-modal-text-muted)", fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.04em' }}>
              {t('expertConfirmFinalScore')}
            </span>
            <span
              style={{ color: accent, fontSize: '2.4rem', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}
              data-testid="expert-confirm-score"
            >
              {count}
            </span>
            <span style={{ color: "var(--wf-modal-text-muted)", fontSize: '0.95rem', fontWeight: 600 }}>/100</span>
          </motion.div>

          {verdictChanged && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
              style={{ color: "var(--wf-modal-text-muted)", fontSize: '0.76rem', marginTop: '0.6rem', fontStyle: 'italic' }}
            >
              {t('expertConfirmVerdictRefined')}
            </motion.p>
          )}
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
};
